/**
 * SearchQuotaManager 组件导出
 *
 * 配额管理标签页，包含仪表板、使用表格和重置功能。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  searchAdminApi,
  type SearchQuotaItem,
} from '../../../services/searchAdminApi';
import { QuotaDashboard } from './QuotaDashboard';
import { QuotaUsageTable } from './QuotaUsageTable';
import { QuotaResetDialog } from './QuotaResetDialog';

interface SearchQuotaManagerProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const SearchQuotaManager: React.FC<SearchQuotaManagerProps> = ({
  autoRefresh = true,
  refreshInterval = 30000,
}) => {
  const [view, setView] = useState<'dashboard' | 'table'>('dashboard');
  const [allQuotas, setAllQuotas] = useState<SearchQuotaItem[]>([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAllQuotas = useCallback(async () => {
    try {
      const result = await searchAdminApi.getQuotaUsage();
      const flat: SearchQuotaItem[] = [];
      for (const [, keys] of Object.entries(result.quotas)) {
        for (const [, quotas] of Object.entries(keys)) {
          flat.push(...quotas);
        }
      }
      setAllQuotas(flat);
    } catch {
      // QuotaDashboard handles its own errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllQuotas();
  }, [fetchAllQuotas]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('dashboard')}
            className={`px-3 py-1.5 text-sm rounded ${
              view === 'dashboard'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            仪表板
          </button>
          <button
            onClick={() => setView('table')}
            className={`px-3 py-1.5 text-sm rounded ${
              view === 'table'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            表格视图
          </button>
        </div>
        <button
          onClick={() => setShowResetDialog(true)}
          className="px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100"
        >
          重置配额
        </button>
      </div>

      {view === 'dashboard' ? (
        <QuotaDashboard
          autoRefresh={autoRefresh}
          refreshInterval={refreshInterval}
        />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">配额使用详情</h2>
          </div>
          <QuotaUsageTable quotas={allQuotas} loading={loading} />
        </div>
      )}

      {showResetDialog && (
        <QuotaResetDialog
          onClose={() => setShowResetDialog(false)}
          onReset={() => {
            fetchAllQuotas();
          }}
        />
      )}
    </div>
  );
};

export { QuotaDashboard } from './QuotaDashboard';
export { QuotaUsageTable } from './QuotaUsageTable';
export { QuotaResetDialog } from './QuotaResetDialog';

export default SearchQuotaManager;
